import { IconEyeOff } from '@tabler/icons-react';
import {
  Text,
  Stack,
  IconButton,
  WP_ICON_NAV,
  EmptyState,
  StateIllustration,
} from '@wapharma/design-system';
import { lazy, Suspense } from 'react';
import { useInboxList } from '../contexts/InboxListContext';
import { useProductBulkImportContextOptional } from '../contexts/ProductBulkImportContext';
import { useCatalogImageResolveInboxContextOptional } from '../contexts/CatalogImageResolveInboxContext';
import { useGtinDumpSyncInboxContextOptional } from '../contexts/GtinDumpSyncInboxContext';
import type { ImportJobStatus } from '../hooks/useCatalogImportJobInbox';
import type { ImageResolveJobStatus } from '../hooks/useCatalogImageResolveJobInbox';
import type { InboxItem } from '../hooks/useInboxListState';
import { formatFriendlyDate } from '../utils/friendlyDate';
import {
  CATALOG_INBOX_OWNER,
  catalogImportAckType,
} from '../utils/catalogImportInboxAck';
import {
  CATALOG_IMAGE_INBOX_OWNER,
  catalogImageResolveAckType,
} from '../utils/catalogImageResolveInboxAck';
import { InboxBulkImportItems } from './InboxBulkImportItems';
import { InboxBulkImageResolveItems } from './InboxBulkImageResolveItems';
import { InboxGtinDumpSyncItems } from './InboxGtinDumpSyncItems';
import { InboxImageResolveQueueItems } from './InboxImageResolveQueueItems';
import { InboxDrillableHint } from './InboxDrillableHint';
import { isCatalogImportInboxType } from './inboxCatalogImport';
import { isCatalogImageResolveInboxType } from './inboxCatalogImageResolve';
import styles from './InboxPopoverContent.module.css';

const CatalogImportInboxDetail = lazy(() =>
  import('./CatalogImportInboxDetail').then((m) => ({ default: m.CatalogImportInboxDetail })),
);
const CatalogImageResolveInboxDetail = lazy(() =>
  import('./CatalogImageResolveInboxDetail').then((m) => ({
    default: m.CatalogImageResolveInboxDetail,
  })),
);
const CatalogImageResolveQueueDetail = lazy(() =>
  import('./CatalogImageResolveQueueDetail').then((m) => ({
    default: m.CatalogImageResolveQueueDetail,
  })),
);
const GtinDumpSyncInboxDetail = lazy(() =>
  import('./GtinDumpSyncInboxDetail').then((m) => ({ default: m.GtinDumpSyncInboxDetail })),
);

export type InboxSelectedDetail =
  | { kind: 'import'; jobId: string }
  | { kind: 'image-resolve'; jobId: string }
  | { kind: 'image-queue' }
  | { kind: 'gtin-dump-sync' };

function isCatalogImportItem(item: InboxItem): boolean {
  return isCatalogImportInboxType(item.type);
}

function isCatalogImageResolveItem(item: InboxItem): boolean {
  return isCatalogImageResolveInboxType(item.type);
}

export function InboxPopoverContent({
  selectedDetail,
  onSelectDetail,
  isImportJobAcked,
  ackImportJob,
  isImageResolveJobAcked,
  ackImageResolveJob,
  isImageResolveQueueSeen,
  ackImageResolveQueue,
}: {
  selectedDetail: InboxSelectedDetail | null;
  onSelectDetail: (detail: InboxSelectedDetail | null) => void;
  isImportJobAcked: (jobId: string, status: ImportJobStatus) => boolean;
  ackImportJob: (jobId: string) => void;
  isImageResolveJobAcked: (jobId: string, status: ImageResolveJobStatus) => boolean;
  ackImageResolveJob: (jobId: string) => void;
  isImageResolveQueueSeen: () => boolean;
  ackImageResolveQueue: () => void;
}) {
  const { items, pending, dismiss, openEntity, markSeen } = useInboxList();
  const bulkImport = useProductBulkImportContextOptional();
  const imageResolveInbox = useCatalogImageResolveInboxContextOptional();
  const inboxImportItemCount = bulkImport?.inboxImportItemCount ?? 0;
  const inboxImageResolveItemCount = imageResolveInbox?.inboxImageResolveItemCount ?? 0;
  const dumpSyncInbox = useGtinDumpSyncInboxContextOptional();
  const inboxDumpSyncItemCount = dumpSyncInbox?.inboxDumpSyncItemCount ?? 0;
  const jobStatus = bulkImport?.importState.jobStatus ?? null;

  const detailOpen = selectedDetail != null;

  const visibleItems = items.filter(
    (i) =>
      !(inboxImportItemCount > 0 && isCatalogImportItem(i)) &&
      !(inboxImageResolveItemCount > 0 && isCatalogImageResolveItem(i)),
  );

  const ackImageResolveQueueFull = async () => {
    ackImageResolveQueue();
  };

  const openImageResolveQueue = () => {
    void ackImageResolveQueueFull();
    onSelectDetail({ kind: 'image-queue' });
  };

  const ackImportJobFull = async (jobId: string, status: ImportJobStatus) => {
    ackImportJob(jobId);
    await markSeen({
      ownerRole: CATALOG_INBOX_OWNER,
      type: catalogImportAckType(status),
      entityId: jobId,
    });
  };

  const ackImageResolveJobFull = async (jobId: string, status: ImageResolveJobStatus) => {
    ackImageResolveJob(jobId);
    await markSeen({
      ownerRole: CATALOG_IMAGE_INBOX_OWNER,
      type: catalogImageResolveAckType(status),
      entityId: jobId,
    });
  };

  const openImportDetail = (jobId: string) => {
    void ackImportJobFull(jobId, jobStatus ?? 'RUNNING');
    onSelectDetail({ kind: 'import', jobId });
  };

  const openImageResolveDetail = (jobId: string, status: ImageResolveJobStatus = 'RUNNING') => {
    void ackImageResolveJobFull(jobId, status);
    onSelectDetail({ kind: 'image-resolve', jobId });
  };

  const dismissImportJob = (jobId: string, status: ImportJobStatus) => {
    void ackImportJobFull(jobId, status);
    void dismiss({
      id: jobId,
      type: catalogImportAckType(status),
      entityType: 'CatalogImportJob',
      entityId: jobId,
      severity: 'medium',
      whyNow: '',
      nextAction: '',
      ownerRole: CATALOG_INBOX_OWNER,
      seen: true,
      seenAt: null,
    });
  };

  const dismissImageResolveJob = (jobId: string, status: ImageResolveJobStatus) => {
    void ackImageResolveJobFull(jobId, status);
    void dismiss({
      id: jobId,
      type: catalogImageResolveAckType(status),
      entityType: 'CatalogImageResolveJob',
      entityId: jobId,
      severity: 'medium',
      whyNow: '',
      nextAction: '',
      ownerRole: CATALOG_IMAGE_INBOX_OWNER,
      seen: true,
      seenAt: null,
    });
  };

  const handleItemClick = (item: InboxItem) => {
    if (!item.seen) {
      void markSeen(item);
    }
    if (isCatalogImportItem(item)) {
      openImportDetail(item.entityId);
      return;
    }
    if (isCatalogImageResolveItem(item)) {
      openImageResolveDetail(item.entityId, 'RUNNING');
      return;
    }
    openEntity(item);
  };

  return (
    <div className={`${styles.shell} ${detailOpen ? styles.shellDetail : ''}`}>
      <div className={styles.viewport}>
        <div className={styles.track}>
          <div className={`${styles.pane} ${styles.listPane}`} aria-hidden={detailOpen}>
            <div className={styles.listTitle}>Boîte de réception</div>
            <InboxBulkImportItems
              onOpenJob={openImportDetail}
              onDismissJob={dismissImportJob}
              isJobSeen={isImportJobAcked}
              onMarkJobSeen={ackImportJobFull}
            />
            <InboxGtinDumpSyncItems
              onOpenDetail={() => onSelectDetail({ kind: 'gtin-dump-sync' })}
            />
            <InboxBulkImageResolveItems
              onOpenJob={(jobId) => openImageResolveDetail(jobId, 'RUNNING')}
              onDismissJob={dismissImageResolveJob}
              isJobSeen={isImageResolveJobAcked}
              onMarkJobSeen={ackImageResolveJobFull}
            />
            <InboxImageResolveQueueItems
              onOpenQueue={openImageResolveQueue}
              isQueueSeen={isImageResolveQueueSeen}
              onMarkQueueSeen={ackImageResolveQueueFull}
            />
            <Stack gap="xs">
              {visibleItems.map((i) => {
                const drillable = isCatalogImportItem(i) || isCatalogImageResolveItem(i);
                const unread = !i.seen;
                const seenMeta = i.seen && i.seenAt ? formatFriendlyDate(i.seenAt) : null;
                return (
                  <div
                    key={i.id}
                    role="button"
                    tabIndex={0}
                    data-inbox-drillable={drillable ? '' : undefined}
                    className={`${styles.inboxItem} ${unread ? styles.inboxItemUnread : styles.inboxItemRead}`}
                    onClick={() => handleItemClick(i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleItemClick(i);
                      }
                    }}
                  >
                    <div className={styles.inboxItemHead}>
                      {unread ? (
                        <span
                          className={`${styles.unreadDot} ${i.severity === 'high' ? styles.unreadDotHigh : ''}`}
                          aria-hidden
                        />
                      ) : null}
                      <div className={styles.inboxItemBody}>
                        <Text size="sm" fw={unread ? 600 : 500} style={{ lineHeight: 1.35 }}>
                          {i.whyNow}
                        </Text>
                        {!drillable && i.nextAction ? (
                          <Text size="xs" c="dimmed" style={{ marginTop: 4 }}>
                            {i.nextAction}
                          </Text>
                        ) : null}
                        {seenMeta ? (
                          <Text
                            size="xs"
                            c="dimmed"
                            style={{ marginTop: 4, fontFamily: 'var(--wp-font-display)' }}
                            title={seenMeta.detail}
                          >
                            Vu · {seenMeta.text}
                          </Text>
                        ) : null}
                      </div>
                      <div className={styles.inboxItemActions}>
                        {drillable ? <InboxDrillableHint /> : null}
                        <IconButton
                          type="button"
                          size="xs"
                          variant="ghost"
                          aria-label="Masquer"
                          disabled={pending === i.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            void dismiss(i);
                          }}
                        >
                          <IconEyeOff {...WP_ICON_NAV} />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Stack>
            {visibleItems.length === 0 &&
            inboxImportItemCount === 0 &&
            inboxImageResolveItemCount === 0 &&
            inboxDumpSyncItemCount === 0 ? (
              <EmptyState
                density="compact"
                title="Boîte de réception vide"
                description="Les imports et alertes apparaîtront ici."
                illustration={<StateIllustration variant="empty" size="sm" tone="soft" />}
              />
            ) : null}
          </div>
          <div className={`${styles.pane} ${styles.detailPane}`}>
            {selectedDetail?.kind === 'import' ? (
              <Suspense
                fallback={
                  <Text size="sm" c="dimmed">
                    Chargement…
                  </Text>
                }
              >
                <CatalogImportInboxDetail
                  jobId={selectedDetail.jobId}
                  onBack={() => onSelectDetail(null)}
                />
              </Suspense>
            ) : null}
            {selectedDetail?.kind === 'image-resolve' ? (
              <Suspense
                fallback={
                  <Text size="sm" c="dimmed">
                    Chargement…
                  </Text>
                }
              >
                <CatalogImageResolveInboxDetail
                  jobId={selectedDetail.jobId}
                  onBack={() => onSelectDetail(null)}
                />
              </Suspense>
            ) : null}
            {selectedDetail?.kind === 'image-queue' ? (
              <Suspense
                fallback={
                  <Text size="sm" c="dimmed">
                    Chargement…
                  </Text>
                }
              >
                <CatalogImageResolveQueueDetail onBack={() => onSelectDetail(null)} />
              </Suspense>
            ) : null}
            {selectedDetail?.kind === 'gtin-dump-sync' ? (
              <Suspense
                fallback={
                  <Text size="sm" c="dimmed">
                    Chargement…
                  </Text>
                }
              >
                <GtinDumpSyncInboxDetail onBack={() => onSelectDetail(null)} />
              </Suspense>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
